import { lazy, Suspense, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { LogOut, RefreshCw } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { contractGateState, useServiceContractStatus } from './api'

const ServiceContractGate = lazy(() => import('./ServiceContractGate'))

function FullScreenSpinner() {
  return (
    <div className="min-h-[100dvh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  )
}

/** Blocks a restaurant admin until the enabled service contract is signed, like the RestaurantApp's IbanActivity. */
export function ServiceContractGuard({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const { t } = useTranslation('serviceContract')
  const restaurantId = user?.role === 'restaurant_admin' ? user.restaurantId : null
  const query = useServiceContractStatus(restaurantId)
  const state = contractGateState(restaurantId, query.data, query.error)

  if (state === 'open') return <>{children}</>
  if (state === 'loading') return <FullScreenSpinner />
  if (state === 'error') {
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="max-w-sm text-muted-foreground">{t('errors.statusFailed')}</p>
        <div className="flex flex-wrap justify-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => void query.refetch()}>
            <RefreshCw className="h-4 w-4" />
            {t('actions.retry')}
          </Button>
          <Button variant="ghost" className="gap-2" onClick={logout}>
            <LogOut className="h-4 w-4" />
            {t('actions.logout')}
          </Button>
        </div>
      </div>
    )
  }
  return (
    <Suspense fallback={<FullScreenSpinner />}>
      <ServiceContractGate restaurantId={restaurantId!} status={query.data!} />
    </Suspense>
  )
}
